import React, { useState, useMemo, useEffect } from 'react';
import {
  FileText, Search, Calendar, X, Eye,
  Truck, Clock
} from 'lucide-react';
import { DELIVERY_STATUS } from '../constants/status';

// 状态徽章
const StatusBadge = ({ status }) => {
  const colorMap = {
    '预约中': 'bg-blue-100 text-blue-700',
    '已送达': 'bg-green-100 text-green-700',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 送货状态徽章
const DeliveryStatusBadge = ({ status }) => {
  const statusConfig = {
    [DELIVERY_STATUS.WAITING_DELIVERY]: { label: '待发货', color: 'bg-blue-100 text-blue-700' },
    [DELIVERY_STATUS.ARRIVED]: { label: '已到场', color: 'bg-indigo-100 text-indigo-700' },
    [DELIVERY_STATUS.WAITING_APPROVAL]: { label: '待审批', color: 'bg-red-100 text-red-700' },
    [DELIVERY_STATUS.STORED]: { label: '已入库', color: 'bg-purple-100 text-purple-700' },
    [DELIVERY_STATUS.PENDING_SETTLEMENT]: { label: '待结算', color: 'bg-orange-100 text-orange-700' },
    [DELIVERY_STATUS.SETTLED]: { label: '已结算', color: 'bg-cyan-100 text-cyan-700' },
    [DELIVERY_STATUS.INVOICED]: { label: '已开票', color: 'bg-teal-100 text-teal-700' },
    [DELIVERY_STATUS.PAID]: { label: '已付款', color: 'bg-green-100 text-green-700' },
  };

  const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

const ArrivalNoticeView = ({ deliveries = [], orders = [], onDetailClick, onOrderClick, onDeliveryClick, initialFilter, setInitialFilter }) => {
  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status) {
        setFilterStatus(initialFilter.status);
      }
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 从 deliveries 提取预到货通知单数据
  const arrivalNotices = useMemo(() => {
    const notices = [];
    deliveries.forEach(delivery => {
      if (delivery.reservation) {
        notices.push({
          id: delivery.reservation.reservationNo,
          docNo: delivery.reservation.reservationNo,
          docType: 'arrival_notice',
          orderId: delivery.orderId,
          orderNo: delivery.orderNo,
          orderStatus: delivery.orderStatus,
          deliveryId: delivery.id,
          deliveryNo: delivery.deliveryNo,
          supplierId: delivery.supplierId,
          supplierName: delivery.supplierName,
          baseId: delivery.baseId,
          baseName: delivery.baseName,
          materialId: delivery.materialId,
          materialName: delivery.materialName,
          price: delivery.price,
          vehicleNo: delivery.reservation.vehicleNo,
          driverName: delivery.reservation.driverName,
          driverPhone: delivery.reservation.driverPhone,
          reservationTime: delivery.reservation.time,
          status: delivery.arrival ? '已送达' : '预约中',
          createdAt: delivery.createdAt,
          delivery,
        });
      }
    });
    return notices;
  }, [deliveries]);

  // 获取唯一的基地和供应商列表
  const { bases, suppliers } = useMemo(() => {
    const baseSet = new Set();
    const supplierSet = new Set();
    arrivalNotices.forEach(doc => {
      if (doc.baseName) baseSet.add(doc.baseName);
      if (doc.supplierName) supplierSet.add(doc.supplierName);
    });
    return {
      bases: Array.from(baseSet).sort(),
      suppliers: Array.from(supplierSet).sort()
    };
  }, [arrivalNotices]);

  // 筛选后的数据
  const filteredNotices = useMemo(() => {
    let result = [...arrivalNotices];

    // 搜索筛选
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc =>
        doc.docNo?.toLowerCase().includes(query) ||
        doc.orderNo?.toLowerCase().includes(query) ||
        doc.deliveryNo?.toLowerCase().includes(query) ||
        doc.supplierName?.toLowerCase().includes(query) ||
        doc.vehicleNo?.toLowerCase().includes(query) ||
        doc.driverName?.toLowerCase().includes(query)
      );
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(doc => doc.baseName === filterBase);
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(doc => doc.supplierName === filterSupplier);
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(doc => doc.status === filterStatus);
    }

    // 日期筛选
    if (filterDateStart) {
      result = result.filter(doc => {
        const docDate = doc.reservationTime?.split(' ')[0];
        return docDate && docDate >= filterDateStart;
      });
    }
    if (filterDateEnd) {
      result = result.filter(doc => {
        const docDate = doc.reservationTime?.split(' ')[0];
        return docDate && docDate <= filterDateEnd;
      });
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [arrivalNotices, searchQuery, filterBase, filterSupplier, filterStatus, filterDateStart, filterDateEnd]);

  // 统计数据
  const stats = useMemo(() => {
    const total = arrivalNotices.length;
    const pending = arrivalNotices.filter(d => d.status === '预约中').length;
    const arrived = arrivalNotices.filter(d => d.status === '已送达').length;
    return { total, pending, arrived };
  }, [arrivalNotices]);

  // 清除所有筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterBase('');
    setFilterSupplier('');
    setFilterStatus('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  const hasActiveFilters = searchQuery || filterBase || filterSupplier || filterStatus || filterDateStart || filterDateEnd;

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">预到货通知管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有预到货通知单</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">全部通知</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <FileText size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">预约中</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Clock size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已送达</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.arrived}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <Truck size={24} className="text-green-500" />
            </div>
          </div>
        </div>
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索单号、供应商、车牌号..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 基地筛选 */}
            <select
              value={filterBase}
              onChange={(e) => setFilterBase(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部基地</option>
              {bases.map(base => (
                <option key={base} value={base}>{base}</option>
              ))}
            </select>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部状态</option>
              <option value="预约中">预约中</option>
              <option value="已送达">已送达</option>
            </select>

            {/* 日期筛选 */}
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <span className="text-slate-400">至</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 数据表格 */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">通知单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">订单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">送货单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">供应商</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">车牌号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">司机</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">联系电话</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">预约时间</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">基地</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">通知状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">送货状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredNotices.length === 0 ? (
                <tr>
                  <td colSpan="12" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <FileText size={40} className="text-slate-300" />
                      <p>暂无预到货通知单数据</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredNotices.map((doc) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="p-3">
                      <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                    </td>
                    <td className="p-3">
                      {onOrderClick ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onOrderClick(doc.orderId);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-mono"
                        >
                          {doc.orderNo}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-600 font-mono">{doc.orderNo}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {onDeliveryClick ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeliveryClick(doc.deliveryId);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-mono"
                        >
                          {doc.deliveryNo}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-600 font-mono">{doc.deliveryNo}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-800 font-medium">{doc.supplierName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 font-mono">{doc.vehicleNo}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{doc.driverName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 font-mono">{doc.driverPhone}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 whitespace-nowrap">{doc.reservationTime}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{doc.baseName}</span>
                    </td>
                    <td className="p-3"><StatusBadge status={doc.status} /></td>
                    <td className="p-3"><DeliveryStatusBadge status={doc.delivery?.status} /></td>
                    <td className="p-3">
                      <button
                        onClick={() => onDetailClick?.(doc)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={12} />
                        详情
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 底部统计 */}
        {filteredNotices.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredNotices.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{arrivalNotices.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrivalNoticeView;
